/**
 * Wires `agent.model.ts` to the reads behind the representations list.
 *
 * Calls `fetchAgentMandates` for the list, `fetchRepresentationRequestsMade`
 * for the Pending tile, and `useWorkThumbnails` for the row images; `/me` is
 * read through `useAccount` for the identity card. There is no write here on
 * purpose — standing down from a mandate lives on the work's own record, where
 * the agent can see what they are giving up.
 *
 * The state the model cannot hold is the chosen artist and the set of
 * collapsed clients. The outbox read fails silently and the mandate read fails
 * loudly, because only one of them is the screen.
 */

import { useCallback, useEffect, useState } from "react";
import type { AgentMandate, RepresentationRequestMade } from "@ondo/shared";

import { fetchAgentMandates, fetchRepresentationRequestsMade } from "../lib/api.ts";
import { useAccount } from "../lib/account.ts";
import { useWorkThumbnails } from "../lib/useWorkThumbnails.ts";
import { describeMandateError, toRepresentationsView } from "./agent.model.ts";
import type { RepresentationsView } from "./agent.model.ts";

export interface RepresentationsController {
  view: RepresentationsView;
  /** Narrow the list to one artist. "" is all of them. */
  chooseArtist: (artist: string) => void;
  /** Fold one client's works away, or unfold them. */
  toggleClient: (ondoId: string) => void;
}

/**
 * The works this account represents.
 *
 * Two reads and their photographs, and no write. There is no write on this
 * screen deliberately: an agent can stand down from a mandate through
 * `ondo.end_work_agent_mandate()`, but doing it from a LIST would be a one-press
 * way to lose access to a record with no sight of what they are giving up. It
 * belongs on the work's own record, beside what the mandate is over.
 *
 * @param workBase '/agent/ONDO-5592/work' — where a row links to.
 * @param orgOndoId the firm whose workspace this is, or null on a personal one.
 */
export function useRepresentationsController(
  workBase: string,
  orgOndoId: string | null,
): RepresentationsController {
  const { me } = useAccount();
  const [mandates, setMandates] = useState<AgentMandate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const images = useWorkThumbnails(mandates, (mandate) => ({
    id: mandate.workId,
    imagePath: mandate.imagePath,
  }));
  const [requests, setRequests] = useState<RepresentationRequestMade[] | null>(null);
  const [artist, setArtist] = useState("");
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set());

  useEffect(() => {
    let active = true;

    fetchAgentMandates()
      .then((found) => {
        if (active) setMandates(found);
      })
      .catch((cause: unknown) => {
        if (active) setError(describeMandateError(cause));
      });

    return () => {
      active = false;
    };
  }, []);

  /**
   * The agent's own outbox, for the PENDING tile alone.
   *
   * Its failure is SWALLOWED, and that is the point: a count of requests is a
   * side fact, and letting a second read decide whether the list of works
   * renders would make the screen fail for a reason that has nothing to do with
   * what it is for. `requests` stays null, `toTiles` drops the tile, and the
   * reader sees three counts instead of four rather than an error page.
   */
  useEffect(() => {
    let active = true;

    fetchRepresentationRequestsMade()
      .then((found) => {
        if (active) setRequests(found);
      })
      .catch(() => undefined);

    return () => {
      active = false;
    };
  }, []);

  const toggleClient = useCallback((ondoId: string) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (!next.delete(ondoId)) next.add(ondoId);
      return next;
    });
  }, []);

  return {
    view: toRepresentationsView({
      mandates,
      error,
      images,
      requests,
      me: me.status === "ready" ? me.me : null,
      orgOndoId,
      workBase,
      artist,
      collapsed,
    }),
    chooseArtist: setArtist,
    toggleClient,
  };
}
